import { connect } from 'redux-bundler-react';
import { useNavigate } from '@tanstack/react-router';
import { useModal, useUserRoleCapabilities } from '@stores';
import { useAuth } from '@hooks';
import { useUpdateStateRegulations } from '@api/services/lookup/hooks';

import ModuleButton from './components/_shared/ModuleButton';
import isAnonymousModal from './components/modals/isAnonymous';
import NewApplicationModal from '../Home/components/modals/NewApplication';

import './resources.scss';
import { Button, Grid, GridContainer } from '@trussworks/react-uswds';
import { statesData } from '@utils/enums';

const rowProps = {
  row: true,
  gap: 'md',
};

const cellProps = {
  desktop: { col: 3 },
  tablet: { col: 6 },
  style: { marginBottom: '1rem' },
};

const Resources = connect('doResetReduxFormData', ({ doResetReduxFormData }) => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { login, isAuthenticated } = useAuth();
  const canCreateRequest = useUserRoleCapabilities((state) => state.roleCapabilities.createRequest);
  const hasLoadedCapabilities = useUserRoleCapabilities((state) => state.hasLoadedRoleCapabilities);
  const updateStateRegulations = useUpdateStateRegulations();
  const canStartRequest = !isAuthenticated || (hasLoadedCapabilities && canCreateRequest);

  const handleViolationComplaintClick = () => {
    doResetReduxFormData();
    !isAuthenticated && updateStateRegulations(statesData);

    openModal(isAnonymousModal);
  };

  const handlePermitsClick = async () => {
    sessionStorage.setItem('PermitButtonSession', 'true');
    if (!isAuthenticated) {
      login();
      return;
    }
    await navigate({ to: '/home' });
    doResetReduxFormData();
    const permitButtonSession = sessionStorage.getItem('PermitButtonSession');
    if (permitButtonSession) {
      openModal(NewApplicationModal);
      sessionStorage.removeItem('PermitButtonSession');
    }
  };

  const handleStartClick = () => {
    isAuthenticated ? navigate({ to: '/' }) : login();
  };

  return (
    <div className='card-menu-container'>
      <div className='hero-container resources'>
        <div className='hero-image' />
        <div className='hero-content'>
          <h1>Welcome to the Regulatory Request System</h1>
          <div className='hero-description'>
            Apply Online • Learn about the Regulatory Program • Track Request Status
          </div>
          <Button onClick={handleStartClick} tabIndex={0}>
            Get Started
          </Button>
        </div>
      </div>
      <GridContainer>
        <Grid {...rowProps}>
          <Grid {...cellProps}>
            <ModuleButton
              title='Start a Request'
              imageAlt='permit application icon'
              text='Submit a Permit Application or other request to the USACE Regulatory Program.'
              isDisabled={!canStartRequest}
              image='site-graphics/Apply-for-Permit.png'
              onClick={handlePermitsClick}
            />
          </Grid>
          <Grid {...cellProps}>
            <ModuleButton
              title='Public Notices'
              imageAlt='public notices icon'
              text='Search, Review, and Comment on USACE Regulatory Public Notices.'
              image='site-graphics/Public-Notices.png'
              onClick={() => navigate({ to: '/public-notices' })}
            />
          </Grid>
          <Grid {...cellProps}>
            <ModuleButton
              title='Report Violations'
              imageAlt='report violations icon'
              text='Submit a report of unauthorized activity or permit non-compliance.'
              isDisabled={!canStartRequest}
              image='site-graphics/Report-Violations.png'
              onClick={handleViolationComplaintClick}
            />
          </Grid>
          <Grid {...cellProps}>
            <ModuleButton
              title='RRS Support'
              imageAlt='rrs help icon'
              text='Get general system support, district contact information, submit feedback, and login.gov help.'
              image='site-graphics/RRS.png'
              onClick={() => navigate({ to: '/support' })}
            />
          </Grid>
          <Grid {...cellProps}>
            <ModuleButton
              title='Regulatory Program Information'
              imageAlt='regulatory program information icon'
              text='Learn the basics about the Regulatory Program, including recent announcements.'
              image='site-graphics/Regulatory-101.png'
              onClick={() => navigate({ to: '/home/$resource', params: { resource: 'regulatory-program' } })}
            />
          </Grid>
          <Grid {...cellProps}>
            <ModuleButton
              image='site-graphics/Mitigation.png'
              imageAlt='mitigation icon'
              onClick={() => navigate({ to: '/home/$resource', params: { resource: 'mitigation' } })}
              text='Discover how we help avoid and minimize impacts to aquatic resources.'
              title='Mitigation Information'
            />
          </Grid>
          <Grid {...cellProps}>
            <ModuleButton
              title='Permitting Information'
              imageAlt='permitting icon'
              text='Do you need a permit? Need to schedule a pre-application meeting?'
              image='site-graphics/Permitting.png'
              onClick={() => navigate({ to: '/home/$resource', params: { resource: 'permitting' } })}
            />
          </Grid>
          <Grid {...cellProps}>
            <ModuleButton
              title='Jurisdiction Information'
              imageAlt='jurisdiction icon'
              text='Does the property in question contain wetlands/waters? If so, does the USACE have jurisdiction?'
              image='site-graphics/Jurisdiction.png'
              onClick={() => navigate({ to: '/home/$resource', params: { resource: 'jurisdiction' } })}
            />
          </Grid>
        </Grid>
      </GridContainer>
    </div>
  );
});

export default Resources;
